import _ from 'underscore';

import AlignmentListingRenderer from 'analyses/alignment/AlignmentListingRenderer';
import JellyfishListingRenderer, {
  JellyfishListingExtras,
} from 'app-core/archetype/sidepanel/JellyfishListingRenderer';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import AnalysisListingRenderer from 'insights/sidepanel/AnalysisListingRenderer';
import AnalyticsDashboardListingRenderer from 'insights/sidepanel/AnalyticsDashboardListingRenderer';
import ConnectionListingRenderer from 'instruments/connections/connection-sidepanel/ConnectionListingRenderer';
import FolderItemListingRenderer, {
  FolderItemColumnExtras,
  FolderItemListingExtras,
} from 'nav/expanded/FolderItemListingRenderer';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  getListingRendererTypeFromModelType,
  ListingRenderer,
  ListingRendererType,
  ListingTypes,
} from 'nav/expanded/ListingRendererConstants';
import CollectibleListingRenderer from 'notebook/entry-templates/collections/sidepanel/CollectibleListingRenderer';
import CollectionListingRenderer from 'notebook/entry-templates/collections/sidepanel/CollectionListingRenderer';
import WorksheetListingRenderer from 'notebook/worksheets/sidepanel/WorksheetListingRenderer';
import AssayRunTaskListingRenderer from 'processes/dashboard/processes-search-platform/listing-renderers/assays/AssayRunTaskListingRenderer';
import AssayStepTaskListingRenderer from 'processes/dashboard/processes-search-platform/listing-renderers/assays/AssayStepTaskListingRenderer';
import AssayUnitOperationTaskListingRenderer from 'processes/dashboard/processes-search-platform/listing-renderers/assays/AssayUnitOperationTaskListingRenderer';
import getListingRendererTypeForProcessTask from 'processes/dashboard/processes-search-platform/listing-renderers/getListingRendererTypeForProcessTask';
import ProcessListingRenderer from 'processes/dashboard/processes-search-platform/listing-renderers/processes/ProcessListingRenderer';
import ProcessOutputListingRenderer from 'processes/dashboard/processes-search-platform/listing-renderers/processes/ProcessOutputListingRenderer';
import ProcessTaskListingRenderer from 'processes/dashboard/processes-search-platform/listing-renderers/processes/ProcessTaskListingRenderer';
import RecipeRunTaskListingRenderer from 'processes/dashboard/processes-search-platform/listing-renderers/recipes/RecipeRunTaskListingRenderer';
import RecipeStepTaskListingRenderer from 'processes/dashboard/processes-search-platform/listing-renderers/recipes/RecipeStepTaskListingRenderer';
import RecipeUnitOperationTaskListingRenderer from 'processes/dashboard/processes-search-platform/listing-renderers/recipes/RecipeUnitOperationTaskListingRenderer';
import RequestV2DefinitionListingRenderer from 'processes/requests_v2/search/RequestV2DefinitionListingRenderer';
import RequestV2SubmissionListingRenderer from 'processes/requests_v2/search/RequestV2SubmissionListingRenderer';
import RequestListingRenderer, {
  RequestColumnExtras,
  RequestListingExtras,
} from 'requests/dashboard/RequestListingRenderer';
import RunListingRenderer, {RunColumnExtras, RunListingExtras} from 'runs/sidepanel/RunListingRenderer';
import TestDefinitionListingRenderer from 'sample-tests/search/TestDefinitionListingRenderer';
import TestOrderListingRenderer from 'sample-tests/search/TestOrderListingRenderer';
import StorableListingRenderer, {
  StorableColumnExtras,
  StorableListingExtras,
} from 'samples/storables/StorableListingRenderer';
import {ListableGroup, ListableItem, Sort} from 'search/SearchConstants';
import StudyListingRenderer from 'studies/sidepanel/StudyListingRenderer';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import {getColumnHeaderSortDirection} from 'ui-library/scrollable/ScrollableTableUtil';
import APIUtil from 'util/APIUtil';
import lazyInit from 'util/lazyInit';
import {BasicEvent} from 'util/Types';
import WorkflowListingRenderer, {
  WorkflowColumnExtras,
  WorkflowListingExtras,
} from 'workflows/components/WorkflowListingRenderer';
import WorklistSidepanelListingRenderer from 'worklists/sidepanel-components/WorklistSidepanelListingRenderer';

import {ProcessTaskListingGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';
import GroupListingRenderer from './GroupListingRenderer';

const getListingRenderers = lazyInit<{[listingType in keyof ListingTypes]: ListingRenderer<listingType>}>(
  () => ({
    [ListingRendererType.ALIGNMENT]: new AlignmentListingRenderer(),
    [ListingRendererType.ANALYSIS]: new AnalysisListingRenderer(),
    [ListingRendererType.ANALYTICS_DASHBOARD]: new AnalyticsDashboardListingRenderer(),
    [ListingRendererType.ASSAY_RUN_TASK]: new AssayRunTaskListingRenderer(),
    [ListingRendererType.ASSAY_STEP_TASK]: new AssayStepTaskListingRenderer(),
    [ListingRendererType.ASSAY_UNIT_OPERATION_TASK]: new AssayUnitOperationTaskListingRenderer(),
    [ListingRendererType.COLLECTIBLE]: new CollectibleListingRenderer(),
    [ListingRendererType.COLLECTION]: new CollectionListingRenderer(),
    [ListingRendererType.CONNECTION]: new ConnectionListingRenderer(),
    [ListingRendererType.FOLDER_ITEM]: new FolderItemListingRenderer(),
    [ListingRendererType.GROUP]: new GroupListingRenderer(),
    [ListingRendererType.JELLYFISH]: new JellyfishListingRenderer(),
    [ListingRendererType.PROCESS]: new ProcessListingRenderer(),
    [ListingRendererType.PROCESS_OUTPUT]: new ProcessOutputListingRenderer(),
    [ListingRendererType.PROCESS_TASK]: new ProcessTaskListingRenderer(),
    [ListingRendererType.RECIPE_RUN_TASK]: new RecipeRunTaskListingRenderer(),
    [ListingRendererType.RECIPE_STEP_TASK]: new RecipeStepTaskListingRenderer(),
    [ListingRendererType.RECIPE_UNIT_OPERATION_TASK]: new RecipeUnitOperationTaskListingRenderer(),
    [ListingRendererType.REQUEST]: new RequestListingRenderer(),
    [ListingRendererType.REQUEST_V2_DEFINITION]: new RequestV2DefinitionListingRenderer(),
    [ListingRendererType.REQUEST_V2_SUBMISSION]: new RequestV2SubmissionListingRenderer(),
    [ListingRendererType.RUN]: new RunListingRenderer(),
    [ListingRendererType.STORABLE]: new StorableListingRenderer(),
    [ListingRendererType.STUDY]: new StudyListingRenderer(),
    [ListingRendererType.TEST_DEFINITION]: new TestDefinitionListingRenderer(),
    [ListingRendererType.TEST_ORDER]: new TestOrderListingRenderer(),
    [ListingRendererType.WORKFLOW]: new WorkflowListingRenderer(),
    [ListingRendererType.WORKLIST]: new WorklistSidepanelListingRenderer(),
    [ListingRendererType.WORKSHEET]: new WorksheetListingRenderer(),
  })
);

function getListingRenderer<T extends keyof ListingTypes>(type: T): ListingRenderer<T> {
  return getListingRenderers()[type];
}

export function getListingRendererType(item: ListableItem): ListingRendererType {
  if (APIUtil.isStudy(item.id)) {
    return ListingRendererType.STUDY;
  } else if (APIUtil.isStorable(item.id)) {
    return ListingRendererType.STORABLE;
  } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.PROCESS) {
    return ListingRendererType.PROCESS;
  } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.PROCESS_TASK) {
    return getListingRendererTypeForProcessTask(item as ProcessTaskListingGraphQL);
  } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.PROCESS_OUTPUT) {
    return ListingRendererType.PROCESS_OUTPUT;
  } else if (APIUtil.isRequestV2Definition(item.id)) {
    return ListingRendererType.REQUEST_V2_DEFINITION;
  } else if (APIUtil.isRequestV2Submission(item.id)) {
    return ListingRendererType.REQUEST_V2_SUBMISSION;
  } else if (APIUtil.isTemplateCollectible(item.id)) {
    return ListingRendererType.COLLECTIBLE;
  } else if (APIUtil.isTemplateCollection(item.id)) {
    return ListingRendererType.COLLECTION;
  } else if (
    // TODO(BNCH-117594): Move this to the last conditional as this is less specific than the others
    APIUtil.isFolderContainable(item.id) ||
    APIUtil.apiIdToType(item.id) === ModelTypeConstants.STAGE_ENTRY
  ) {
    return ListingRendererType.FOLDER_ITEM;
  } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.WORKFLOW) {
    return ListingRendererType.WORKFLOW;
  } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.REQUEST) {
    return ListingRendererType.REQUEST;
  } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.ASSAY_RUN) {
    return ListingRendererType.RUN;
  } else if (APIUtil.isAnalysis(item.id)) {
    return ListingRendererType.ANALYSIS;
  } else if (APIUtil.isAnalyticsDashboard(item.id)) {
    return ListingRendererType.ANALYTICS_DASHBOARD;
  } else if (APIUtil.isSequenceAnalysis(item.id) || APIUtil.isProteinAlignment(item.id)) {
    return ListingRendererType.ALIGNMENT;
  } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.CONNECTION) {
    return ListingRendererType.CONNECTION;
  } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.JELLYFISH) {
    return ListingRendererType.JELLYFISH;
  } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.WORKLIST) {
    return ListingRendererType.WORKLIST;
  } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.WORKSHEET) {
    return ListingRendererType.WORKSHEET;
  } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.TEST_DEFINITION) {
    return ListingRendererType.TEST_DEFINITION;
  } else if (APIUtil.apiIdToType(item.id) === ModelTypeConstants.TEST_ORDER) {
    return ListingRendererType.TEST_ORDER;
  }
  throw new Error(`Unsupported listing type for item ${item.id}`);
}

interface RowDataById {
  [itemId: string]: {[accessorKey: string]: unknown};
}

interface GroupRowDataById {
  [groupId: string]: RowDataById;
}

interface IconsById {
  [itemId: string]: JSX.Element;
}

interface RowContentHeightById {
  [itemId: string]: number;
}

interface GroupRowContentHeightById {
  [groupId: string]: RowContentHeightById;
}

interface RowClickHandlersById {
  [id: string]: <U extends ListingRendererType>(
    item: ListingTypes[U],
    openItem: (item: ListingTypes[U]) => void
  ) => void;
}

interface RenderForTypeResult {
  rowDataByItemId: RowDataById;
  iconsByItemId: IconsById;
  rowContentHeightByItemId: RowContentHeightById;
  onClickHandlersById: RowClickHandlersById;
}

export interface RenderResult {
  columns: Array<ScrollableTableColumn>;
  rowDataByItemId: RowDataById;
  groupRowDataByItemId: GroupRowDataById;
  iconsByItemId: IconsById;
  rowContentHeightByItemId: RowContentHeightById;
  groupRowContentHeightByItemId: GroupRowContentHeightById;
  clickHandlersByItemId: RowClickHandlersById;
}

interface ItemsByType {
  [index: string]: Array<ListableItem>;
}

interface GroupItemsByType {
  [groupId: string]: ItemsByType;
}

/**
 * ListingExtras are used to determine which columns to include in the expanded listing
 * and to populate the cell contents.
 */
export type ListingExtras = FolderItemListingExtras &
  StorableListingExtras &
  WorkflowListingExtras &
  RequestListingExtras &
  RunListingExtras &
  JellyfishListingExtras;

/**
 * ColumnExtras are the subset of ListingExtras used to determine which columns to include in the expanded listing.
 * We use it when we want to only get the columns without actually rendering anything.
 */
type ColumnExtras = FolderItemColumnExtras &
  StorableColumnExtras &
  WorkflowColumnExtras &
  RequestColumnExtras &
  RunColumnExtras;

export interface ContextMenuExtras {
  finderId: string;
  isListing: boolean;
  actionSource: ActionOrigin;
}

export function openExpandedContextMenu<T extends keyof ListingTypes>(
  item: ListingTypes[T],
  e: BasicEvent,
  extras: ContextMenuExtras
): void {
  const type = getListingRendererType(item as ListableItem);
  const listingRenderer = getListingRenderer(type);
  listingRenderer.openContextMenu(item, e, extras);
}

/**
 * Concatenates all items of a given type into a single array for operations that need to factor in group
 * children and top level items (such as determining which columns to create).
 */
function getAllItemsForType<T extends ListingRendererType>(
  type: T,
  itemsByType: ItemsByType,
  groupItemsByType: ItemsByType,
  groupChildItemsByType: GroupItemsByType
): Array<ListingTypes[T]> {
  const topLevelItemsForType = itemsByType[type] ?? [];
  const groupItemsForType = groupItemsByType[type] ?? [];
  return topLevelItemsForType.concat(
    ...groupItemsForType,
    Object.values(groupChildItemsByType).flatMap((groupChildItemsMap) => groupChildItemsMap[type] ?? [])
  ) as Array<ListingTypes[T]>;
}

/**
 * Creates a list of columns for purposes for rendering the sort menu.
 *
 * Tech Debt: Logic should be shared with the render() function below.
 */
export function makeColumnsForSortMenu(
  items: Array<ListableItem>,
  sorts: Array<Sort>,
  extras: ColumnExtras,
  typeFilterModelTypes: Array<string>,
  groups?: Array<ListableGroup>
): Array<ScrollableTableColumn> {
  let columns: Array<ScrollableTableColumn> = [];

  const itemsByType = _.groupBy(items, getListingRendererType);
  const groupChildItemsByType: GroupItemsByType = {};
  for (const group of groups ?? []) {
    groupChildItemsByType[group.id] = _.groupBy(group.items, getListingRendererType);
  }
  // This and the call to getAllItemsForType explicitly set the groupItems to an empty object since users
  // can't actually sort by group columns, so we don't want to include those objects in the set we use to
  // determine available sort keys.
  const typeFilterListingRendererTypes = _.compact(
    typeFilterModelTypes.map((modelType) => getListingRendererTypeFromModelType(modelType))
  );
  const sortedTypes = [
    ...getSortedTypes(itemsByType, {}, groupChildItemsByType),
    ...typeFilterListingRendererTypes,
  ];
  for (const type of sortedTypes) {
    const listingRenderer = getListingRenderer(type);
    const allItemsForType = getAllItemsForType(type, itemsByType, {}, groupChildItemsByType);
    const columnConfigurations = listingRenderer.getColumnConfigurations(allItemsForType, extras);
    const typeColumns = getColumnsFromColumnConfigurations(
      columnConfigurations,
      type,
      allItemsForType,
      sorts,
      extras.isRegistry
    );

    columns = columns.concat(typeColumns);
  }

  return normalizeColumns(sortedTypes, columns, extras.isRegistry);
}

/**
 * Sorts types by the total number of items of that type across top level items and all group children.
 */
function getSortedTypes(
  itemsByType: ItemsByType,
  groupItemsByType: ItemsByType,
  groupChildItemsByType: GroupItemsByType
): Array<ListingRendererType> {
  const countsByType = new Map<string, number>();
  Object.keys(itemsByType).forEach((type) => {
    countsByType.set(type, itemsByType[type].length);
  });

  Object.keys(groupItemsByType).forEach((type) => {
    countsByType.set(type, groupItemsByType[type].length);
  });

  Object.values(groupChildItemsByType).forEach((groupTypes) => {
    Object.keys(groupTypes).forEach((type) => {
      countsByType.set(type, (countsByType.get(type) ?? 0) + groupTypes[type].length);
    });
  });

  return _.sortBy(Array.from(countsByType.keys()), (type) =>
    countsByType.get(type)
  ).reverse() as Array<ListingRendererType>;
}

function getColumnsForType<T extends ListingRendererType>(
  type: T,
  items: Array<ListingTypes[T]>,
  sorts: Array<Sort>,
  extras: ListingExtras
): {
  columnConfigurations: Array<ColumnConfiguration<ListingTypes[T]>>;
  columns: Array<ScrollableTableColumn>;
} {
  const listingRenderer = getListingRenderer(type);
  const columnConfigurations = listingRenderer.getColumnConfigurations(items, extras);
  const columns = getColumnsFromColumnConfigurations(
    columnConfigurations,
    type,
    items,
    sorts,
    extras.isRegistry
  );

  return {
    columnConfigurations,
    columns,
  };
}

function renderForType<T extends ListingRendererType>(
  type: T,
  items: Array<ListingTypes[T]>,
  columnConfigurations: Array<ColumnConfiguration<ListingTypes[T]>>,
  extras: ListingExtras
): RenderForTypeResult {
  const listingRenderer = getListingRenderer(type);

  const rowDataByItemId: RowDataById = {};
  const rowContentHeightByItemId: RowContentHeightById = {};
  const iconsByItemId: IconsById = {};
  const onClickHandlersById: RowClickHandlersById = {};
  for (const item of items) {
    rowDataByItemId[item.id] = {};
    for (const columnConfiguration of columnConfigurations) {
      if (!columnConfiguration.isHidden || !columnConfiguration.isHidden(item)) {
        rowDataByItemId[item.id][columnConfiguration.column.accessorKey] = columnConfiguration.render(item);
      }
    }
    rowContentHeightByItemId[item.id] = listingRenderer.getRowContentHeight(item, extras);
    iconsByItemId[item.id] = listingRenderer.renderIcon(item, extras);
    if (listingRenderer.onClick != null) {
      onClickHandlersById[item.id] = ((item: ListingTypes[T], openItem: (item: ListingTypes[T]) => void) => {
        listingRenderer.onClick!(item, openItem);
      }) as RowClickHandlersById[string];
    }
  }

  return {
    rowDataByItemId,
    rowContentHeightByItemId,
    iconsByItemId,
    onClickHandlersById,
  };
}

function renderGroupItemsForType<T extends ListingRendererType>(
  type: T,
  groups: Array<ListableGroup>,
  columnConfigurations: Array<ColumnConfiguration<ListingTypes[T]>>,
  extras: ListingExtras
): RenderForTypeResult {
  const listingRenderer = getListingRenderer(type);

  const rowDataByItemId: RowDataById = {};
  const rowContentHeightByItemId: RowContentHeightById = {};
  const iconsByItemId: IconsById = {};
  const onClickHandlersById: RowClickHandlersById = {};
  for (const group of groups) {
    rowDataByItemId[group.id] = {};

    const item = group.groupItem! as ListingTypes[T];

    for (const columnConfiguration of columnConfigurations) {
      if (!columnConfiguration.isHidden || !columnConfiguration.isHidden(item)) {
        rowDataByItemId[group.id][columnConfiguration.column.accessorKey] = columnConfiguration.render(item);
      }
    }
    rowContentHeightByItemId[group.id] = listingRenderer.getRowContentHeight(item, extras);
    iconsByItemId[group.id] = listingRenderer.renderIcon(item, extras);
    if (listingRenderer.onClick != null) {
      onClickHandlersById[item.id] = ((item: ListingTypes[T], openItem: (item: ListingTypes[T]) => void) => {
        listingRenderer.onClick!(item, openItem);
      }) as RowClickHandlersById[string];
    }
  }

  return {
    rowDataByItemId,
    rowContentHeightByItemId,
    iconsByItemId,
    onClickHandlersById,
  };
}
function getColumnsFromColumnConfigurations<T extends ListingRendererType>(
  columnConfigurations: Array<ColumnConfiguration<ListingTypes[T]>>,
  type: T,
  items: Array<ListingTypes[T]>,
  sorts: Array<Sort>,
  preferIdForPrimaryCol?: boolean
): Array<ScrollableTableColumn> {
  const columns = columnConfigurations
    .filter(
      (column: ColumnConfiguration<ListingTypes[T]>) =>
        !column.isHidden || items.some((item) => !column.isHidden!(item))
    )
    .map((columnConfiguration: ColumnConfiguration<ListingTypes[T]>) => ({
      ...columnConfiguration.column,
      sortDirection: columnConfiguration.column.isSortable
        ? getColumnHeaderSortDirection(sorts, columnConfiguration.column.sortKey!)
        : null,
    }));
  return normalizeColumns([type], columns, preferIdForPrimaryCol);
}

/**
 * Deduplicate all columns, and position the "Name" and "ID" columns appropriately.
 * @package
 */
export function normalizeColumns(
  sortedTypes: Array<ListingRendererType>,
  columns: Array<ScrollableTableColumn>,
  preferIdForPrimaryCol?: boolean,
  forGroupedTable?: boolean
): Array<ScrollableTableColumn> {
  if (sortedTypes.length === 1 && !forGroupedTable) {
    return columns;
  }

  // Early return here to skip adding default Name or ID columns if they don't already exist. This lets us
  // determine if a set of search results would generate any columns to render by checking for ANY columns
  // instead of something like (columns.length === 1 && [ID_COLUMN, NAME_COLUMN].includes(columns[0])) which
  // is more brittle.
  if (columns.length === 0) {
    return columns;
  }

  // If the listing has objects of different types, there may be multiple "Name" and "ID" columns with different headers.
  // To only show one "Name" and/or "ID" column with a single header, we take the first column with the given accessor key
  // and use that as the singular "Name" and/or "ID" column.
  const getColumnByAccessorKey = (
    columns: Array<ScrollableTableColumn>,
    key: ColumnAccessorKey.NAME | ColumnAccessorKey.ID
  ): ScrollableTableColumn | null => {
    const matchingColumns = columns.filter((column) => column.accessorKey === key);
    // Prefer non-fixed column if both are present
    const nonFixedMatchingColumn = matchingColumns.find((column) => !column.isFixed);
    const column = nonFixedMatchingColumn || (matchingColumns.length > 0 ? matchingColumns[0] : null);
    return column
      ? {
          ...column,
          header: key === ColumnAccessorKey.NAME ? 'Name' : 'ID',
        }
      : null;
  };

  let combinedColumns = [];
  const starColumn = columns.filter((column) => column.accessorKey === ColumnAccessorKey.STAR)[0];
  const idColumn = getColumnByAccessorKey(columns, ColumnAccessorKey.ID);
  const nameColumn = getColumnByAccessorKey(columns, ColumnAccessorKey.NAME);
  if (preferIdForPrimaryCol && !forGroupedTable) {
    combinedColumns = [idColumn, starColumn, nameColumn, ...columns];
  } else {
    combinedColumns = [nameColumn, starColumn, idColumn, ...columns];
  }
  // eslint-disable-next-line no-restricted-syntax
  return _.uniq(_.compact(combinedColumns), ({accessorKey}) => accessorKey);
}

/**
 * Renders the specified columns for the supplied items, and adds the rendered values to the row data
 * objects provided.
 */
function addRenderedValues<T extends ListingRendererType>(
  type: T,
  items: Array<ListingTypes[T]>,
  columnConfigurations: Array<ColumnConfiguration<ListingTypes[T]>>,
  extras: ListingExtras,
  rowDataByItemId: RowDataById,
  rowContentHeightByItemId: RowContentHeightById,
  iconsByItemId: IconsById,
  clickHandlerByItemId?: RowClickHandlersById
): void {
  const {
    rowDataByItemId: groupRowDataById,
    rowContentHeightByItemId: groupContentHeightById,
    iconsByItemId: groupIconsById,
    onClickHandlersById,
  } = renderForType(type, items, columnConfigurations, extras);

  Object.assign(rowDataByItemId, groupRowDataById);
  Object.assign(rowContentHeightByItemId, groupContentHeightById);
  Object.assign(iconsByItemId, groupIconsById);
  if (clickHandlerByItemId != null) {
    Object.assign(clickHandlerByItemId, onClickHandlersById);
  }
}

/**
 * Renders the specified columns for the supplied items for groups that are entity links, and adds the
 * rendered values to the row data objects provided for the groups.
 * This is separate from addRenderedValues because:
 * - The item is nested within the group, but we need to associate it with the group's id, not the item's.
 * - Group data will already have been rendered and we need to merge the two objects together.
 */
function addRenderedGroupItemsValues<T extends ListingRendererType>(
  type: T,
  groups: Array<ListableGroup>,
  columnConfigurations: Array<ColumnConfiguration<ListingTypes[T]>>,
  extras: ListingExtras,
  rowDataByItemId: RowDataById,
  rowContentHeightByItemId: RowContentHeightById,
  iconsByItemId: IconsById
): void {
  const {
    rowDataByItemId: groupRowDataById,
    rowContentHeightByItemId: groupContentHeightById,
    iconsByItemId: groupIconsById,
  } = renderGroupItemsForType(type, groups, columnConfigurations, extras);

  Object.entries(groupRowDataById).forEach(([groupId, groupRowData]) => {
    rowDataByItemId[groupId] = {
      ...groupRowData,
      ...rowDataByItemId[groupId],
    };
  });
  // The GroupListingRenderer uses the default height as a constant, so the additional attributes will only
  // increase that height or keep it the same. It's therefore safe to just overwrite the value for these
  // items here.
  Object.assign(rowContentHeightByItemId, groupContentHeightById);
  Object.assign(iconsByItemId, groupIconsById);
}

export default function render(
  items: Array<ListableItem>,
  sorts: Array<Sort>,
  extras: ListingExtras,
  groups?: Array<ListableGroup>
): RenderResult {
  let columns: Array<ScrollableTableColumn> = [];
  const rowDataByItemId: RowDataById = {};
  const clickHandlersByItemId: RowClickHandlersById = {};
  const rowContentHeightByItemId: RowContentHeightById = {};
  const iconsByItemId: IconsById = {};
  const groupChildItemsByType: GroupItemsByType = {};
  const groupRowDataByItemId: GroupRowDataById = {};
  const groupRowContentHeightByItemId: GroupRowContentHeightById = {};

  if (groups?.length) {
    // Add these first so that any columns for aggregations and the like are arranged right after name/id.
    const {columnConfigurations: groupColumnConfigurations, columns: groupColumns} = getColumnsForType(
      ListingRendererType.GROUP,
      groups,
      sorts,
      extras
    );
    columns = columns.concat(groupColumns);

    addRenderedValues(
      ListingRendererType.GROUP,
      groups,
      groupColumnConfigurations,
      extras,
      rowDataByItemId,
      rowContentHeightByItemId,
      iconsByItemId
    );

    for (const group of groups) {
      if (group.isExpanded) {
        groupChildItemsByType[group.id] = _.groupBy(group.items, getListingRendererType);
      }
      groupRowDataByItemId[group.id] = {};
      groupRowContentHeightByItemId[group.id] = {};
    }
  }

  // If we are grouping by an entity link field, and any of the groups are for a single entity, we want to
  // add the additional metadata fields for those items to the group rows. To ensure we set the column
  // orders using the same logic as the child items, we include them in the data set that we create
  // sortedTypes from.
  // groupItemsToGroup allows us to map the group item back to the group to render it properly. This is
  // needed because we need to merge the columns into the group's row data instead of associating it with
  // the linked item's id.
  const groupItemsToGroup = new Map<ListableItem, ListableGroup>(
    groups?.filter((group) => group.groupItem).map((group) => [group.groupItem!, group]) ?? []
  );
  const groupItems = [...groupItemsToGroup.keys()];
  const groupItemsByType = _.groupBy(groupItems, getListingRendererType);

  const itemsByType = _.groupBy(items, getListingRendererType);

  const sortedTypes = getSortedTypes(itemsByType, groupItemsByType, groupChildItemsByType);
  for (const type of sortedTypes) {
    const {columns: typeColumns, columnConfigurations: typeColumnConfigurations} = getColumnsForType(
      type,
      getAllItemsForType(type, itemsByType, groupItemsByType, groupChildItemsByType),
      sorts,
      extras
    );
    columns = columns.concat(typeColumns);

    if (itemsByType[type]) {
      addRenderedValues(
        type,
        itemsByType[type] as Array<ListingTypes[ListingRendererType]>,
        typeColumnConfigurations,
        extras,
        rowDataByItemId,
        rowContentHeightByItemId,
        iconsByItemId,
        clickHandlersByItemId
      );
    }

    if (groupItemsByType[type]) {
      const groupsForType = groupItemsByType[type].map((groupItem) => groupItemsToGroup.get(groupItem)!);
      addRenderedGroupItemsValues(
        type,
        groupsForType,
        typeColumnConfigurations,
        extras,
        rowDataByItemId,
        rowContentHeightByItemId,
        iconsByItemId
      );
    }

    for (const group of groups ?? []) {
      if (group.isExpanded && groupChildItemsByType[group.id][type]) {
        addRenderedValues(
          type,
          groupChildItemsByType[group.id][type] as Array<ListingTypes[ListingRendererType]>,
          typeColumnConfigurations,
          extras,
          groupRowDataByItemId[group.id],
          groupRowContentHeightByItemId[group.id],
          // Icons can't vary by group, since they are purely based on item type, so no need for a separate
          // map for this bit.
          iconsByItemId,
          clickHandlersByItemId
        );
      }
    }
  }

  columns = normalizeColumns(sortedTypes, columns, extras.isRegistry, (groups?.length ?? 0) > 0);

  return {
    columns,
    rowDataByItemId,
    groupRowDataByItemId,
    rowContentHeightByItemId,
    groupRowContentHeightByItemId,
    iconsByItemId,
    clickHandlersByItemId,
  };
}
